import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {ColorPath, Radius, Shadow} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {getCssColorByPath} from '../../../utils/colors'

interface Props extends React.HTMLProps<HTMLDivElement> {
  /** Card content */
  children?: React.ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Whether card should have rounded corners at the top */
  hasRoundedTop?: boolean
  /** Whether card should have rounded corners at the bottom */
  hasRoundedBottom?: boolean
  /** Radius of rounded corners */
  borderRadius?: Radius
  /** Shows up 1px border with specified color */
  borderColorPath?: ColorPath
  /** Sets background color of Card component */
  backgroundColorPath?: ColorPath
  /** Whether card should have shadow */
  shadow?: Shadow
  /** Whether overflowing content of the card should be hidden */
  isOverflowHidden?: boolean
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledCard = styled(Card)` */
  style?: React.CSSProperties
}

interface StyledCardProps extends Props {
  backgroundColorValue?: string
  borderColorValue?: string
}

const CardElement = styled.div<StyledCardProps>`
  background: ${({backgroundColorValue}) =>
    backgroundColorValue ?? cssTheme.colors.background.neutral.primary};
  overflow: ${({isOverflowHidden}) =>
    isOverflowHidden ? 'hidden' : 'visible'};
  border: ${({borderColorValue}) =>
    borderColorValue ? `1px solid ${borderColorValue}` : 'none'};
  box-shadow: ${({shadow}) => (shadow ? cssTheme.shadows[shadow] : 'none')};

  &.--has-rounded-top {
    border-top-left-radius: ${({borderRadius}) =>
      borderRadius ? cssTheme.layout.radius[borderRadius] : 0};
    border-top-right-radius: ${({borderRadius}) =>
      borderRadius ? cssTheme.layout.radius[borderRadius] : 0};
  }
  &.--has-rounded-bottom {
    border-bottom-left-radius: ${({borderRadius}) =>
      borderRadius ? cssTheme.layout.radius[borderRadius] : 0};
    border-bottom-right-radius: ${({borderRadius}) =>
      borderRadius ? cssTheme.layout.radius[borderRadius] : 0};
  }
`

export const Card = ({
  children,
  dataId,
  hasRoundedTop = true,
  hasRoundedBottom = true,
  borderRadius,
  borderColorPath,
  backgroundColorPath,
  shadow,
  isOverflowHidden = true,
  className,
  style,
  id
}: Props) => {
  const borderColorValue = borderColorPath && getCssColorByPath(borderColorPath)
  const backgroundColorValue =
    backgroundColorPath && getCssColorByPath(backgroundColorPath)

  return (
    <CardElement
      data-id={dataId}
      className={cx(
        className,
        hasRoundedTop && '--has-rounded-top',
        hasRoundedBottom && '--has-rounded-bottom'
      )}
      borderRadius={borderRadius}
      borderColorValue={borderColorValue}
      backgroundColorValue={backgroundColorValue}
      isOverflowHidden={isOverflowHidden}
      shadow={shadow}
      style={style}
      id={id}
    >
      {children}
    </CardElement>
  )
}
